import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb } from 'lucide-react';
import { usePreferences } from '@/context/PreferencesContext';
import './celebration.css';

/**
 * CuriosityCelebration - Complete implementation with force close support
 * 
 * Key improvements:
 * - Uses framer-motion for animation timeline control
 * - Sparkles generated once, not in React state
 * - Force close support to immediately stop animations
 * - Proper cleanup of all timers
 */
const CuriosityCelebration = forwardRef(({ points, gems, onComplete, forceClose = false }, ref) => {
  const containerRef = useRef(null);
  const hasCompletedRef = useRef(false);
  const timersRef = useRef([]);
  const [showFlyingPoints, setShowFlyingPoints] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const { playCelebrationSound } = usePreferences();

  // Generate sparkles once (not in state to avoid re-renders)
  const sparkles = Array.from({ length: 20 }, (_, i) => {
    const angle = (i * 360) / 20;
    const angleRad = (angle * Math.PI) / 180;
    const distance = 80 + Math.random() * 40;
    return {
      id: `sparkle-${i}`,
      x: Math.cos(angleRad) * distance,
      y: Math.sin(angleRad) * distance,
      delay: i * 0.1 + Math.random() * 0.2,
    };
  });

  // Expose forceClose method via ref
  useImperativeHandle(ref, () => ({
    forceClose: () => {
      setIsVisible(false);
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current = [];
      hasCompletedRef.current = true;
      if (onComplete) {
        onComplete();
      }
    },
  }), [onComplete]);

  // Handle force close
  useEffect(() => {
    if (forceClose) {
      setIsVisible(false);
      // Clear all timers
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current = [];
      if (onComplete) {
        onComplete();
      }
    }
  }, [forceClose, onComplete]);

  useEffect(() => {
    // Don't start animation if force closed
    if (forceClose) {
      return;
    }

    // Play celebration sound when animation starts
    if ((points > 0 || gems > 0) && playCelebrationSound) {
      playCelebrationSound();
    }

    // Show flying points animation after message is displayed
    const flyingPointsTimer = setTimeout(() => {
      if (!forceClose && !hasCompletedRef.current) {
        setShowFlyingPoints(true);
      }
    }, 500);
    timersRef.current.push(flyingPointsTimer);

    // Auto-complete after animation duration
    const timer = setTimeout(() => {
      if (!hasCompletedRef.current && !forceClose && onComplete) {
        hasCompletedRef.current = true;
        onComplete();
      }
    }, 1000); // Total animation duration
    timersRef.current.push(timer);

    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current = [];
    };
  }, [onComplete, points, gems, playCelebrationSound, forceClose]);

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.div
          ref={containerRef}
          className="celebration-container"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
        >
          {/* Lightbulb with glow animation */}
          <motion.div
            className="curiosity-bulb-container"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0, y: -50 }}
            transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15,
              duration: 0.6,
            }}
          >
            <motion.div
              className="curiosity-bulb"
              animate={{
                scale: [1, 1.1, 1],
                filter: [
                  'drop-shadow(0 0 20px rgba(251, 191, 36, 0.8))',
                  'drop-shadow(0 0 40px rgba(251, 191, 36, 1))',
                  'drop-shadow(0 0 20px rgba(251, 191, 36, 0.8))',
                ],
              }}
              transition={{
                duration: 1,
                repeat: 1,
                ease: 'easeInOut',
              }}
            >
              <Lightbulb className="w-32 h-32 text-yellow-400" fill="currentColor" />
            </motion.div>
          </motion.div>

          {/* Sparkles around lightbulb */}
          <AnimatePresence>
            {sparkles.map((sparkle) => (
              <motion.div
                key={sparkle.id}
                className="curiosity-sparkle"
                style={{
                  left: '50%',
                  top: '50%',
                  transform: `translate(calc(-50% + ${sparkle.x}px), calc(-50% + ${sparkle.y}px))`,
                }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                  opacity: [0, 1, 0],
                  scale: [0, 1, 0],
                  y: [0, -40, -80],
                }}
                transition={{
                  delay: sparkle.delay,
                  duration: 0.6 + Math.random() * 0.3,
                  ease: 'easeOut',
                }}
              />
            ))}
          </AnimatePresence>

          {/* Points display - initial */}
          {!showFlyingPoints && (
            <motion.div
              className="celebration-points"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <div className="points-badge">
                <span className="points-icon">💎</span>
                <span className="points-text">+{points} XP</span>
                {gems > 0 && <span className="points-text">· +{gems} Gems</span>}
              </div>
            </motion.div>
          )}

          {/* Flying points animation to top-right */}
          <AnimatePresence>
            {showFlyingPoints && (
              <>
                {/* XP Points flying to top-right */}
                <motion.div
                  className="flying-points"
                  initial={{
                    x: 0,
                    y: 0,
                    scale: 1,
                    opacity: 1,
                  }}
                  animate={{
                    x: typeof window !== 'undefined' ? (window.innerWidth / 2) - 120 : 0,
                    y: typeof window !== 'undefined' ? -(window.innerHeight / 2) + 100 : 0,
                    scale: 0.6,
                    opacity: 0,
                  }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: 0.8,
                    ease: [0.25, 0.46, 0.45, 0.94], // easeOutQuad
                  }}
                >
                  <div className="flying-points-badge">
                    <span className="points-icon">💎</span>
                    <span className="points-text">+{points}</span>
                  </div>
                </motion.div>

                {/* Gems flying to top-right (if applicable) */}
                {gems > 0 && (
                  <motion.div
                    className="flying-points"
                    initial={{
                      x: 0,
                      y: 0,
                      scale: 1,
                      opacity: 1,
                    }}
                    animate={{
                      x: typeof window !== 'undefined' ? (window.innerWidth / 2) - 60 : 0,
                      y: typeof window !== 'undefined' ? -(window.innerHeight / 2) + 100 : 0,
                      scale: 0.6,
                      opacity: 0,
                    }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.8,
                      delay: 0.1,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                  >
                    <div className="flying-points-badge gems">
                      <span className="points-icon">💎</span>
                      <span className="points-text">+{gems}</span>
                    </div>
                  </motion.div>
                )}
              </>
            )}
          </AnimatePresence>

          {/* Congratulatory message */}
          <motion.div
            className="celebration-message curiosity-message"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            <h2 className="message-title">💡 Great Job!</h2>
            <p className="message-subtitle">You completed the curiosity activity!</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

CuriosityCelebration.displayName = 'CuriosityCelebration';

export default CuriosityCelebration;
